import * as Sentry from "@sentry/react";
import { getInstallId } from "./installId";
import { containerLog, CONTAINER_LOG } from "./containerLog";
import { reloadApp } from "./reloadApp";

// GPU-reset watchdog. On the Windows fleet, a GPU reset/TDR (driver hang,
// DX10_BBHANG) leaves the page alive but undrawn — a permanent whitescreen
// that a plain reload cures. The driver fires `webglcontextlost` on any WebGL
// context the page holds, so a hidden 1x1 context detects exactly this
// condition and automates the reload. Ships through the content-update
// channel: every machine gets it without a container deployment.
//
// Two probes, because one is not enough on this fleet. A driver that Chromium
// has blocklisted for WebGL — the natural fate of the broken Intel driver that
// causes the TDRs — still composites through the GPU, so the resets continue
// while `getContext("webgl")` returns null and a WebGL-only watchdog silently
// never arms. Chromium ≥ 99 also dispatches `contextlost` on an accelerated 2D
// canvas when the GPU process goes away, so a second, 2D probe covers exactly
// that machine. Which probes armed is tagged on every event and reported once
// per install when WebGL is missing, so "no resets from machine X" can be told
// apart from "machine X cannot report resets".
//
// The watchdog is also the Phase-0 measurement instrument for
// docs/gpu-load-investigation.md: every reset is reported to Sentry and to
// container telemetry with the install id and the active route, so remediation
// can be judged against a per-machine-per-day reset rate. Anything that
// suppresses a report biases that baseline — see the ordering note below.
const RELOAD_COUNT_KEY = "mrgGpuResetReloads";
const LIFETIME_COUNT_KEY = "mrgGpuResetTotal";
const ARM_STATE_REPORTED_KEY = "mrgGpuWatchdogArmStateReported";
const MAX_RELOADS_PER_SESSION = 3;
// Chromium only GPU-accelerates a 2D canvas above a minimum size; a 1x1 probe
// would be rasterised on the CPU and never lose its context. 256x256 is well
// over the threshold and costs ~256KB of texture.
const PROBE_2D_SIZE = 256;
// One GPU reset kills every context the page holds at once, so both probes
// fire for the same event; the second signal inside this window is the same
// reset, not a new one.
const DUPLICATE_WINDOW_MS = 2000;

// Module-level so the detached canvases (and their listeners) cannot be
// garbage collected — the watchdog must live as long as the page.
let watchdogCanvas = null;
let watchdogContext = null;
let watchdog2dCanvas = null;
let watchdog2dContext = null;
let lastResetAt = 0;

// Which screen was on-GPU when the driver hung. Momentum runs under
// HashRouter, so its route is the hash and the pathname is a constant (under
// file:// it is the whole on-disk path); the web build is the reverse. A
// HashRouter route always begins `#/`, which is what distinguishes it from a
// plain anchor like `#pricing` on the web build — keying off "has a hash"
// instead would report the anchor as the route. Query strings are dropped:
// they carry ids and tokens, and the metric groups by screen.
const activeRoute = () => {
    try {
        const hash = window.location.hash || "";
        const route = hash.startsWith("#/") ? hash.slice(1) : window.location.pathname;
        return (route || "unknown").split("?")[0];
    } catch {
        return "unknown";
    }
};

// Resets survive the reload, so a per-session counter alone understates a
// machine that resets repeatedly across boots. This cumulative figure rides
// along in each report, giving the container-log reader a running total
// without having to join events.
const bumpLifetimeCount = () => {
    try {
        const next = (Number(localStorage.getItem(LIFETIME_COUNT_KEY)) || 0) + 1;
        localStorage.setItem(LIFETIME_COUNT_KEY, String(next));
        return next;
    } catch {
        return null;
    }
};

const reportReset = ({
    installId,
    route,
    lifetimeCount,
    sessionReloadsUsed,
    willReload,
    source,
}) => {
    // Container telemetry: one line per reset, self-describing so the metric can
    // be recovered from logs alone. Message prefix and field order are what the
    // Phase-0 runbook documents — append, never reorder.
    containerLog(
        `GPU context lost - install=${installId ?? "unknown"} ` +
            `route=${route} sessionReloadsUsed=${sessionReloadsUsed} ` +
            `lifetimeResets=${lifetimeCount ?? "unknown"} ` +
            `action=${willReload ? "reloading" : "reload-limit-reached"} ` +
            `source=${source}`,
        CONTAINER_LOG.WARN
    );

    // Sentry: tags are what make this queryable as a rate. `gpu_reset` is the
    // filter, `install_id` the per-machine grouping, `route` the attribution.
    // The fingerprint pins every reset into a single issue regardless of
    // tag values, so the per-day event count on that one issue *is* the metric.
    Sentry.withScope((scope) => {
        scope.setTag("gpu_reset", "true");
        scope.setTag("install_id", installId ?? "unknown");
        scope.setTag("route", route);
        scope.setTag("gpu_reset_action", willReload ? "reloading" : "reload-limit-reached");
        scope.setTag("gpu_reset_source", source);
        scope.setContext("gpu_reset", {
            route,
            installId,
            sessionReloadsUsed,
            lifetimeCount,
            willReload,
            source,
            devicePixelRatio: window.devicePixelRatio,
            viewport: `${window.innerWidth}x${window.innerHeight}`,
        });
        scope.setFingerprint(["gpu-context-lost"]);
        // Message text unchanged — Phase 0 alerting already keys on it.
        Sentry.captureMessage("GPU context lost — reloading to recover", "warning");
    });
};

// Shared by both probes. `source` says which context reported the loss.
const onContextLost = (source) => {
    const now = Date.now();
    if (now - lastResetAt < DUPLICATE_WINDOW_MS) return;
    lastResetAt = now;

    // Report BEFORE the rate limit decides anything. This ordering is the
    // whole point: previously an unreadable counter, or the 4th reset in a
    // session, returned early and the reset was never counted — so the
    // worst-affected machines contributed the least data to the baseline.
    const installId = getInstallId();
    const route = activeRoute();
    const lifetimeCount = bumpLifetimeCount();

    // Hard rate limit: a crash-looping GPU must not become a reload loop.
    // If the counter is unreadable, don't reload — an unbounded loop is
    // worse than an unrecovered whitescreen — but still report.
    let reloads = null;
    try {
        reloads = Number(sessionStorage.getItem(RELOAD_COUNT_KEY)) || 0;
    } catch {
        reloads = null;
    }
    const willReload = reloads !== null && reloads < MAX_RELOADS_PER_SESSION;

    reportReset({
        installId,
        route,
        lifetimeCount,
        // Reloads already spent this session — not a reset ordinal; once
        // the cap is hit this stops advancing while lifetimeCount does.
        sessionReloadsUsed: reloads === null ? "unknown" : reloads,
        willReload,
        source,
    });

    if (!willReload) return;
    try {
        sessionStorage.setItem(RELOAD_COUNT_KEY, String(reloads + 1));
    } catch {
        return;
    }

    // Backoff: 3s, then 6s, then 9s — gives a wedged driver time to reset
    // and the Sentry event time to flush before the document unloads.
    setTimeout(() => {
        reloadApp("gpu-context-lost");
    }, 3000 * (reloads + 1));
};

const armWebglProbe = () => {
    const canvas = document.createElement("canvas");
    canvas.width = 1;
    canvas.height = 1;
    let gl = null;
    try {
        gl = canvas.getContext("webgl");
    } catch {
        gl = null;
    }
    if (!gl) return false;

    canvas.addEventListener("webglcontextlost", (event) => {
        event.preventDefault();
        onContextLost("webgl");
    });
    watchdogCanvas = canvas;
    watchdogContext = gl;
    return true;
};

const arm2dProbe = () => {
    // Without the event there is nothing to listen for; don't claim coverage.
    if (!("oncontextlost" in HTMLCanvasElement.prototype)) return false;
    const canvas = document.createElement("canvas");
    canvas.width = PROBE_2D_SIZE;
    canvas.height = PROBE_2D_SIZE;
    let ctx = null;
    try {
        ctx = canvas.getContext("2d");
        // The backing store is allocated lazily on first draw; until then
        // there is no GPU resource to lose.
        ctx?.fillRect(0, 0, 1, 1);
    } catch {
        ctx = null;
    }
    if (!ctx) return false;

    // Not cancelled: a cancelled 2D contextlost suppresses restoration, and we
    // are reloading anyway.
    canvas.addEventListener("contextlost", () => onContextLost("canvas2d"));
    watchdog2dCanvas = canvas;
    watchdog2dContext = ctx;
    return true;
};

// Once per install per arm state — not per boot — so a machine that cannot
// see GPU resets is counted exactly once in Sentry instead of flooding it.
const reportArmStateOnce = (armState, installId) => {
    let alreadyReported = false;
    try {
        alreadyReported = localStorage.getItem(ARM_STATE_REPORTED_KEY) === armState;
        localStorage.setItem(ARM_STATE_REPORTED_KEY, armState);
    } catch {
        alreadyReported = false;
    }
    if (alreadyReported) return;

    containerLog(
        `GPU watchdog arm state - install=${installId ?? "unknown"} armed=${armState}`,
        CONTAINER_LOG.INFO
    );
    Sentry.withScope((scope) => {
        scope.setTag("gpu_watchdog", armState);
        scope.setFingerprint(["gpu-watchdog-arm-state", armState]);
        Sentry.captureMessage(
            `GPU watchdog: WebGL unavailable on this install (armed=${armState})`,
            "info"
        );
    });
};

export const startGpuResetWatchdog = () => {
    if (watchdogCanvas || watchdog2dCanvas) return;

    const webgl = armWebglProbe();
    const canvas2d = arm2dProbe();
    const armState =
        webgl && canvas2d ? "webgl+canvas2d" : webgl ? "webgl" : canvas2d ? "canvas2d" : "none";

    // On every event, so any crash from a machine says whether GPU resets
    // there are observable at all. (install_id itself is set in instrument.js.)
    try {
        Sentry.setTag("gpu_watchdog", armState);
    } catch {
        /* telemetry must never block the watchdog from arming */
    }
    if (!webgl) reportArmStateOnce(armState, getInstallId());

    // Manual-test hook, dev server only. A real GPU reset kills every WebGL
    // context at once — that is what makes this watchdog work — but it also
    // means the reset cannot be provoked from the console: these canvases are
    // module-private and never enter the DOM, and calling WEBGL_lose_context on
    // some *other* context will not fire their listeners.
    // `import.meta.env.DEV` is false in every `vite build`, so this is dead
    // code eliminated from the fleet and web bundles alike.
    if (import.meta.env.DEV) {
        window.__gpuResetWatchdogCanvas = watchdogCanvas;
        window.__gpuResetWatchdog2dCanvas = watchdog2dCanvas;
    }
};
